"use client";
import Image from "next/image";
import { useRef, type CSSProperties } from "react";
import { Maximize2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { productCaptures } from "./ProductEvidence";
import styles from "./MissionPlanningEvidence.module.css";

type Evidence = Readonly<{ id: string; label: string; caption: string; alt: string; crop: Readonly<{x:number;y:number;width:number;height:number}> }>;
/** CSS-only crop of an unchanged product capture; the full original remains inspectable. */
export default function MissionPlanningEvidence({ evidence }: { evidence: Evidence }) {
  const dialog = useRef<HTMLDialogElement>(null);
  const capture = productCaptures[evidence.id];
  const crop = evidence.crop;
  const cropStyle = {
    aspectRatio: `${crop.width} / ${crop.height}`,
    "--capture-width": `${capture.width / crop.width * 100}%`,
    "--capture-left": `${-crop.x / crop.width * 100}%`,
    "--capture-top": `${-crop.y / crop.height * 100}%`,
  } as CSSProperties;
  return <figure className="min-w-0" data-planning-capture={evidence.id}>
    <div className="relative">
      <div className={styles.viewport} tabIndex={0} role="region" aria-label={`${evidence.label}. Scroll to inspect image details.`}>
        <div className={styles.canvas} style={cropStyle}>
          <Image src={capture.src} alt={evidence.alt} width={capture.width} height={capture.height} sizes="(max-width:767px) 1400px, (min-width:1280px) 1200px, 100vw" className={styles.image} />
        </div>
      </div>
      <Button variant="outline" size="icon" type="button" className="absolute bottom-2 right-2 h-11 w-11 bg-white/95 text-primary shadow-sm hover:bg-blue-50" aria-label={`Maximize image: ${evidence.label}`} aria-haspopup="dialog" onClick={() => dialog.current?.showModal()}><Maximize2 aria-hidden="true" className="h-5 w-5" /></Button>
    </div>
    <figcaption className="mt-3 text-sm leading-6 text-slate-600">{evidence.caption}</figcaption>
    <dialog ref={dialog} aria-label={evidence.label} className="product-evidence-dialog fixed m-auto w-[96vw] max-w-[1600px] overflow-hidden rounded-xl border border-slate-200 bg-white p-0 text-primary shadow-xl backdrop:bg-slate-950/70" onClick={event => {if(event.target === event.currentTarget) dialog.current?.close();}}>
      <div className="flex items-center justify-between gap-3 border-b border-slate-200 px-4 py-2"><p className="text-sm font-semibold">Full-size image — scroll to inspect details</p><Button variant="outline" autoFocus onClick={() => dialog.current?.close()}>Close image</Button></div>
      <div className="max-h-[72dvh] overflow-auto overscroll-contain" tabIndex={0} aria-label="Full-size image — scroll to inspect details"><Image src={capture.original} alt={evidence.alt} width={capture.originalWidth} height={capture.originalHeight} unoptimized className="h-auto max-w-none" style={{width:capture.originalWidth}} /></div>
      <p className="px-4 py-3 text-sm leading-6">{evidence.caption}</p>
    </dialog>
  </figure>;
}
